/**
 * Pure helpers for the recruiter speed-to-lead section (Reports -> Metrics;
 * owner request 2026-10-01). The rules behind the numbers are the server's
 * (crm.recruiter_speed_stats); this file only formats and labels them. It
 * imports types only, so its test needs no mock.
 */
import type { SpeedMeasure, SpeedMeasureStats, SpeedStats, SpeedThreshold } from '../../types';

/** getPref/setPref key (stored as crm.reports.speedMode). */
export const SPEED_MODE_PREF = 'reports.speedMode';
export const DEFAULT_SPEED_MODE: SpeedMeasure = 'business';
export const SPEED_THRESHOLD_KEYS: SpeedThreshold[] = ['15m', '1h', '4h', '1d'];

export const SPEED_MODE_OPTIONS: Array<{ value: SpeedMeasure; label: string; title: string }> = [
  { value: 'business', label: 'Business hours', title: 'Count only the open hours set in Settings → Booking' },
  { value: 'clock', label: 'All hours', title: 'Count every hour, nights and weekends included' },
];

export function isSpeedMeasure(v: unknown): v is SpeedMeasure {
  return v === 'business' || v === 'clock';
}

/** "1h 5m" from seconds of at least an hour, carrying a rounded 60 minutes. */
function hoursMinutes(s: number): string {
  let h = Math.floor(s / 3600);
  let m = Math.round((s % 3600) / 60);
  if (m === 60) {
    h += 1;
    m = 0;
  }
  return m ? `${h}h ${m}m` : `${h}h`;
}

/**
 * A wait in the chosen measure. Business time stays in hours ("68h": a
 * business day is not 24 hours, so "2d" would mislead); clock time rolls into
 * days past 24 hours ("2d 20h"). A zero wait reads "0m"; none reads "—".
 */
export function fmtWait(sec: number | null | undefined, measure: SpeedMeasure): string {
  if (sec === null || sec === undefined || !Number.isFinite(Number(sec))) return '—';
  const s = Math.max(0, Math.round(Number(sec)));
  if (s === 0) return '0m';
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.round(s / 60);
    return m === 60 ? '1h' : `${m}m`;
  }
  if (measure === 'business' || s < 86400) return hoursMinutes(s);
  let d = Math.floor(s / 86400);
  let h = Math.round((s % 86400) / 3600);
  if (h === 24) {
    d += 1;
    h = 0;
  }
  return h ? `${d}d ${h}h` : `${d}d`;
}

/** Whole-percent share, or null when nothing is known yet at that threshold. */
export function sharePct(cell: { hit: number; of: number } | null | undefined): number | null {
  if (!cell || !cell.of) return null;
  return Math.round((100 * cell.hit) / cell.of);
}

export function fmtShare(cell: { hit: number; of: number } | null | undefined): string {
  const p = sharePct(cell);
  return p === null ? '—' : `${p}%`;
}

export function thresholdLabel(key: SpeedThreshold, measure: SpeedMeasure): string {
  switch (key) {
    case '15m':
      return 'Within 15 min';
    case '1h':
      return 'Within 1 hour';
    case '4h':
      return 'Within 4 hours';
    case '1d':
      return measure === 'business' ? 'Within 1 business day' : 'Within 24 hours';
    default:
      return key;
  }
}

/** "1 hour" style word for a threshold inside a sentence. */
export function thresholdWords(key: SpeedThreshold, measure: SpeedMeasure): string {
  return thresholdLabel(key, measure).replace(/^Within /, '');
}

export function measureStats(stats: SpeedStats, measure: SpeedMeasure): SpeedMeasureStats {
  return stats[measure];
}

export function pathLabel(key: 'web' | 'agent'): string {
  return key === 'agent' ? 'Texting agent hand-offs' : 'Website leads';
}

export function pathHint(key: 'web' | 'agent'): string {
  return key === 'agent'
    ? 'Clock starts when the agent hands the lead to the team'
    : 'Clock starts when the form comes in';
}

/** The recruiter column: a name, a former teammate, or the shared pool. */
export function recruiterLabel(row: { key: string | null; name: string | null }): string {
  if (row.name) return row.name;
  return row.key ? 'Former teammate' : 'Unassigned';
}

export function studyLabel(row: { key: string | null; name: string | null }): string {
  if (row.name) return row.name;
  return row.key ? 'Unknown study' : 'No CRM study';
}

/** The muted line under the headline cards: the leads outside the timing. */
export function asideLine(stats: SpeedStats): string {
  const parts: string[] = [];
  const add = (v: number, phrase: string) => {
    if (v) parts.push(`${v.toLocaleString()} ${phrase}`);
  };
  add(stats.reached_early, 'reached before it was the team’s turn');
  add(stats.closed, 'closed before anyone reached out');
  add(stats.with_agent, 'not handed over by the texting agent');
  add(stats.booked_later, 'booked a call for later');
  return parts.join(' · ');
}

/** The business-hours line above the cards, or the all-hours note. */
export function modeNote(measure: SpeedMeasure, hours: { label: string; timezone: string; closed_dates: number } | null | undefined): string {
  if (measure === 'clock') return 'Counting every hour, nights and weekends included.';
  if (!hours) return 'Counting business hours only.';
  const closed = hours.closed_dates ? `, minus ${hours.closed_dates.toLocaleString()} closed date${hours.closed_dates === 1 ? '' : 's'}` : '';
  return `Counting business hours only: ${hours.label} (${hours.timezone})${closed}.`;
}
